import React, { useState } from 'react';
import { ArrowTopRightOnSquareIcon, StarIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';

const RACHEL_REVIEW = `"Praying the Psalms by J.P. Pezzoli is the book I've been searching for over the past two or three years. I've read several books on prayer, including one on praying through the Psalms, but none of them guided me through the actual process in a practical, accessible way. Pezzoli does exactly that.

In Praying the Psalms, Pezzoli presents each passage of Scripture with helpful context before inviting the reader to pause, reflect, and pray. Every Psalm includes multiple sample prayers that demonstrate how to respond personally to the text. The entries are short enough that reading one or two each day feels manageable, making it easy to build a consistent habit. Pezzoli strikes an excellent balance between being approachable for new believers while remaining faithful to the depth and truth of Scripture.

This book also came into my life at exactly the right time. After moving home from the mission field, I found it difficult to reestablish healthy spiritual rhythms. My time in God's Word had become inconsistent, my prayer life was struggling, and I often felt abandoned by God. I wanted to return to Scripture but also needed help learning how to pray again.

This book didn't magically fix everything, but it has helped me begin praying consistently again, spend time in God's Word each day, and remember that even David experienced seasons of grief, confusion, and lament. That reminder alone was deeply encouraging.

I highly recommend Praying the Psalms to anyone who feels stuck in their prayer life or wants to develop a richer, more Scripture-centered approach to prayer. Whether you're new to praying through the Psalms or have been doing it for years, this is a thoughtful, practical resource that I believe many Christians will benefit from."`;

const PRODUCTS = [
  {
    id: 1,
    title: 'Praying The Psalms, Book 1',
    subtitle: 'A Prayer Guide for the Distracted and Weary',
    author: 'JP Pezzoli',
    format: 'Paperback',
    price: '$14.99',
    rating: 5.0,
    reviewCount: 2,
    image: 'https://m.media-amazon.com/images/I/81VnvI7vQWL._SL1500_.jpg',
    description:
      'A guided invitation to slow down and meet God in Scripture. Walks you through Psalms 1–41, helping distracted and weary believers encounter God through the words of the Psalms — one step at a time. Each psalm includes the full text, guided pauses, reflective insights, and a written prayer drawn from the passage itself.',
    tag: 'Prayer & Devotion',
    tagColor: 'bg-cerulean/10 text-cerulean dark:bg-cerulean/20',
    url: 'https://www.amazon.com/dp/B0GX2VMRT9',
    review: {
      author: 'Rachel Darden',
      title: 'Exactly What I Needed for My Prayer Life',
      verified: true,
      body: RACHEL_REVIEW,
    },
  },
  {
    id: 2,
    title: 'Every Story Kids Bible: Genesis',
    subtitle: 'The Beginning',
    author: 'JP Pezzoli',
    format: 'Paperback',
    price: '$18.49',
    rating: 4.6,
    reviewCount: 83,
    image: 'https://m.media-amazon.com/images/I/71T8DSl+t7L._SL1293_.jpg',
    description:
      'Tells every story in Genesis — from creation and the fall, to the flood and the lives of Abraham, Isaac, Jacob, and Joseph. Children discover the moments often left untold, and begin to see how each story connects to the next, forming one continuous story from beginning to end. Perfect for family reading, bedtime, or personal devotion.',
    tag: "Children's Bible",
    tagColor: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    url: 'https://www.amazon.com/dp/B0GW995SQS',
  },
  {
    id: 3,
    title: 'Among',
    subtitle: 'Building a Community on Mission',
    author: 'David J. Pezzoli & Chris L. DiDonna',
    format: 'Paperback',
    price: '$11.97',
    rating: 5.0,
    reviewCount: 3,
    image: 'https://m.media-amazon.com/images/I/41ePNDze9hL._SL1360_.jpg',
    description:
      'Every Christian is called to be a missionary wherever they live, work, and play. This handbook helps you unlock the power of the Body of Christ — to create and lead environments that facilitate the growth of the local church and the advancement of the Kingdom of God. For anyone who desires to see God move in their neighborhoods, campuses, and cities.',
    tag: 'Community & Mission',
    tagColor: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    url: 'https://www.amazon.com/dp/153764226X',
  },
];

function StarRating({ rating, count }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5">
        {Array.from({ length: full }).map((_, i) => (
          <StarIcon key={`f${i}`} className="w-4 h-4 text-amber-400" />
        ))}
        {half && (
          <div className="relative w-4 h-4">
            <StarOutlineIcon className="absolute inset-0 w-4 h-4 text-amber-400" />
            <div className="absolute inset-0 overflow-hidden w-1/2">
              <StarIcon className="w-4 h-4 text-amber-400" />
            </div>
          </div>
        )}
        {Array.from({ length: empty }).map((_, i) => (
          <StarOutlineIcon key={`e${i}`} className="w-4 h-4 text-amber-300 dark:text-amber-600" />
        ))}
      </div>
      <span className="text-sm text-gray-500 dark:text-white/40">
        {rating.toFixed(1)} ({count} {count === 1 ? 'review' : 'reviews'})
      </span>
    </div>
  );
}

function ReviewExpander({ review }) {
  const [expanded, setExpanded] = useState(false);
  const preview = review.body.slice(0, 120).trimEnd() + '…';

  return (
    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-white/8">
      {/* Reviewer line */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <div className="flex gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <StarIcon key={i} className="w-3 h-3 text-amber-400" />
          ))}
        </div>
        <span className="text-xs font-semibold text-gray-700 dark:text-white/70">{review.author}</span>
        {review.verified && (
          <span className="text-xs text-emerald-600 dark:text-emerald-400">· Verified Purchase</span>
        )}
      </div>

      <p className="text-xs font-semibold text-gray-700 dark:text-white/60 mb-1 italic">"{review.title}"</p>

      {/* Body — collapsed or expanded */}
      <div className="text-xs text-gray-500 dark:text-white/40 leading-relaxed">
        {expanded ? (
          <p className="whitespace-pre-line">{review.body}</p>
        ) : (
          <p>{preview}</p>
        )}
      </div>

      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-1.5 flex items-center gap-0.5 text-xs text-cerulean hover:text-cerulean/80 font-medium transition-colors"
      >
        {expanded ? 'Show less' : 'Read full review'}
        <ChevronDownIcon className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
    </div>
  );
}

function ProductCard({ product }) {
  return (
    <div className="bg-white dark:bg-[#111827] rounded-2xl shadow-sm border border-gray-200/60 dark:border-white/10 overflow-hidden flex flex-col group hover:shadow-lg hover:border-cerulean/30 dark:hover:border-cerulean/30 transition-all duration-300">
      {/* Book cover */}
      <div className="relative bg-gray-50 dark:bg-white/5 flex items-center justify-center p-8 min-h-[260px]">
        <img
          src={product.image}
          alt={product.title}
          className="max-h-52 w-auto object-contain drop-shadow-xl group-hover:scale-105 transition-transform duration-300"
        />
        <span className={`absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full ${product.tagColor}`}>
          {product.tag}
        </span>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1">
        <div className="mb-3">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-snug">{product.title}</h3>
          {product.subtitle && (
            <p className="text-sm text-gray-500 dark:text-white/50 mt-0.5 italic">{product.subtitle}</p>
          )}
          <p className="text-sm text-cerulean dark:text-cerulean mt-1 font-medium">{product.author}</p>
        </div>

        <StarRating rating={product.rating} count={product.reviewCount} />

        <p className="text-sm text-gray-600 dark:text-white/60 mt-4 leading-relaxed flex-1">
          {product.description}
        </p>

        <div className="mt-6 flex items-center justify-between">
          <div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">{product.price}</span>
            <span className="ml-2 text-xs text-gray-400 dark:text-white/30">{product.format}</span>
          </div>
          <a
            href={product.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-cerulean hover:bg-cerulean/85 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"
          >
            Buy on Amazon
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          </a>
        </div>

        {product.review && <ReviewExpander review={product.review} />}
      </div>
    </div>
  );
}

export default function Shop() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0d1520] pb-20 lg:pb-8">
      {/* Header */}
      <div className="bg-white dark:bg-[#111827] border-b border-gray-200/60 dark:border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <div className="inline-flex items-center gap-2 bg-cerulean/10 dark:bg-cerulean/20 text-cerulean text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
            Books & Resources
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            Deepen Your Family's Faith
          </h1>
          <p className="text-gray-500 dark:text-white/50 max-w-xl mx-auto text-base leading-relaxed">
            Books written by the people behind Lineage Bible — to help you pray, read Scripture with your kids, and build community on mission.
          </p>
        </div>
      </div>

      {/* Product grid */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PRODUCTS.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-400 dark:text-white/25 mt-12">
          Purchases are made through Amazon. Lineage Bible is not responsible for third-party fulfillment or pricing.
        </p>
      </div>
    </div>
  );
}
